/* ==========================================================================
   Product card behaviour: pack selection and add to cart.

   Both are progressive enhancements over markup that already works. The variant
   buttons are real buttons and the form is a real product form, so with this
   file blocked the card still adds to the cart — it just does it with a page
   load, and the pack pills fall back to the card's default variant.

   One delegated listener for the whole document rather than one per card. A
   collection page can carry forty of these and forty pairs of listeners is
   forty things to tear down when a section re-renders in the theme editor.
   ========================================================================== */
(function () {
  'use strict';

  /* Load-once guard. The script is included by more than one section now (the
     collection grid and the homepage "Most played" rail); two copies would bind
     two submit listeners and add every item to the cart twice. */
  if (window.__csPcardBound) return;
  window.__csPcardBound = true;

  var ADD_URL = (window.routes && window.routes.cart_add_url) || '/cart/add';

  function closestCard(el) {
    return el.closest ? el.closest('.cs-pcard') : null;
  }

  /* ---------------------------------------------------------------------
     Pack selection — swap the id the form posts, and the price shown.
     --------------------------------------------------------------------- */
  function selectVariant(button) {
    var card = closestCard(button);
    if (!card) return;

    card.querySelectorAll('[data-cs-variant]').forEach(function (b) {
      var on = b === button;
      b.classList.toggle('is-selected', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });

    var idField = card.querySelector('[data-cs-atc-id]');
    if (idField) idField.value = button.dataset.csVariant;

    var price = card.querySelector('[data-cs-price]');
    if (price && button.dataset.price) price.textContent = button.dataset.price;

    // Compare-at and the saving travel together: a strikethrough with no
    // percentage next to it reads as a mistake.
    var compare = card.querySelector('[data-cs-compare]');
    var save = card.querySelector('[data-cs-save]');
    var hasOffer = !!button.dataset.compare;

    if (compare) {
      compare.textContent = hasOffer ? button.dataset.compare : '';
      compare.hidden = !hasOffer;
    }
    if (save) {
      save.textContent = hasOffer ? '(' + button.dataset.save + '% OFF)' : '';
      save.hidden = !hasOffer;
    }

    // The festival price block (only printed while the sale is enabled). The
    // deal row's [hidden] is its on/off switch — cs-pcard.css shows the box
    // and drops the regular row off it — so it follows the offer too. The box
    // reads the whole-rupee copies; data-price keeps the ".00" the regular
    // row has always shown.
    var deal = card.querySelector('.cs-fest-deal');
    if (deal) deal.hidden = !hasOffer;
    var pct = card.querySelector('[data-cs-fest-pct]');
    if (pct && hasOffer) pct.textContent = button.dataset.save + '% OFF';
    var boxPrice = card.querySelector('[data-cs-box-price]');
    if (boxPrice && button.dataset.boxPrice) boxPrice.textContent = button.dataset.boxPrice;
    var boxCompare = card.querySelector('[data-cs-box-compare]');
    if (boxCompare) boxCompare.textContent = hasOffer ? button.dataset.boxCompare : '';

    var atc = card.querySelector('[data-cs-atc-button]');
    if (atc) atc.disabled = button.dataset.available === 'false';
  }

  /* ---------------------------------------------------------------------
     Which cart UI to render into.

     Dawn ships two, and which one exists is decided by the CART TYPE setting:
     `drawer` renders <cart-drawer>, `notification` renders <cart-notification>,
     and `page` renders neither. This shop is on `drawer`, so <cart-notification>
     is never in the document.

     That is what sent every card add-to-cart to /cart. This function used to
     look up <cart-notification> alone and bail out when it was missing, and
     bailing out means no preventDefault, so the browser posted the form the
     old-fashioned way and landed on the cart page. The drawer was built,
     mounted and working — the cards were the only thing not talking to it.

     Both elements expose the same two methods, so the drawer is preferred, the
     notification is honoured if the setting is ever changed, and `page` still
     falls through to a normal form post, which is the correct behaviour there.
     --------------------------------------------------------------------- */
  function cartTarget() {
    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.renderContents === 'function') return drawer;

    var notification = document.querySelector('cart-notification');
    if (notification && typeof notification.renderContents === 'function') return notification;

    return null;
  }

  /* ---------------------------------------------------------------------
     Add to cart — post it, then hand the response to the cart UI the theme
     is configured for, so the card does not invent a second cart UI.
     --------------------------------------------------------------------- */
  function addToCart(form, event) {
    var target = cartTarget();

    // Nothing to render into, so let the browser submit the form and land on
    // the cart page.
    if (!target) return;

    event.preventDefault();

    var button = form.querySelector('[data-cs-atc-button]');
    var label = form.querySelector('[data-cs-atc-label]');
    var original = label ? label.textContent : '';

    if (button) {
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');
    }

    var body = new FormData(form);
    if (typeof target.getSectionsToRender === 'function') {
      body.append('sections', target.getSectionsToRender().map(function (s) { return s.id; }));
      body.append('sections_url', window.location.pathname);
    }

    fetch(ADD_URL, {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: body
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data.status) {
          // Shopify answers an out-of-stock or invalid line with a status code
          // in the body rather than an HTTP error.
          if (label) label.textContent = data.description || data.message || 'Unavailable';
          return;
        }
        // `renderContents` clears `is-empty` from the drawer's inner panel but
        // NOT from <cart-drawer> itself, and the element-level class is the one
        // that drives the empty-cart layout: it hides `.drawer__header`, which
        // is where the title and the CLOSE BUTTON live, and switches the panel
        // to a single centred row so the line items collapse. A shopper whose
        // cart was empty on page load would open the drawer, see no item and no
        // way out of it. Dawn's own product form clears it for the same reason
        // (assets/product-form.js) — the cards have to do it too.
        if (target.classList.contains('is-empty')) target.classList.remove('is-empty');

        target.renderContents(data);
      })
      .catch(function () {
        // Network failed. Fall back to the thing that always works.
        form.submit();
      })
      .then(function () {
        if (button) {
          button.classList.remove('is-loading');
          button.removeAttribute('aria-busy');
        }
        if (label && label.textContent !== original) {
          setTimeout(function () { label.textContent = original; }, 2400);
        }
      });
  }

  document.addEventListener('click', function (e) {
    var variant = e.target.closest && e.target.closest('[data-cs-variant]');
    if (variant) {
      e.preventDefault();
      selectVariant(variant);
    }
  });

  document.addEventListener('submit', function (e) {
    var form = e.target.closest && e.target.closest('[data-cs-atc]');
    if (form) addToCart(form, e);
  });

  var reduceMotion = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : { matches: false };

  /* ---------------------------------------------------------------------
     Big Play Fest card dressing: sale-switch fallback, photo frame and
     sparkles, and the once-a-second tick.

     Moved here from an inline <script> the card snippet printed on every
     card. Same behaviour, same switch: it only runs where a card carries
     data-cs-fest-end, which the snippet sets exactly when it used to print
     the script (fest enabled and a NEW list set). The window flag still
     stops a second copy if an old inline one is ever on the page.
     --------------------------------------------------------------------- */
  (function festCards() {
    var flag = document.querySelector('.cs-pcard[data-cs-fest-end]');
    if (!flag || window.__csFestCards) return;
    window.__csFestCards = 1;

    var h = document.documentElement;
    var START = Date.parse(flag.getAttribute('data-cs-fest-start'));
    var END = Date.parse(flag.getAttribute('data-cs-fest-end'));

    // The sale switch normally comes from cs-fest.liquid's head script, which
    // sets data-cs-fest first; this only acts if that script is missing.
    if (!h.getAttribute('data-cs-fest')) {
      h.setAttribute('data-cs-fest', '1');
      var now = Date.now();
      var preview = /[?&]fest_preview=1(&|$)/.test(location.search) ||
        (window.Shopify && Shopify.theme && Shopify.theme.role && Shopify.theme.role !== 'main');
      if ((now >= START && now <= END) || preview) {
        var live = function () { h.classList.add('cs-fest-live'); };
        live();
        document.addEventListener('DOMContentLoaded', live);
        window.addEventListener('load', live);
      }
    }

    // Photo frame + four sparkles per card (shown by CSS only while live).
    var SPARKS = [['10px', '42%', null], ['44%', null, '8px'], [null, null, '14%', '12px'], ['30%', '8px', null]];
    function deco(card) {
      if (card.__csf) return;
      card.__csf = 1;
      var media = card.querySelector('.cs-pcard__media');
      if (media) {
        if (getComputedStyle(media).position === 'static') media.style.position = 'relative';
        var frame = document.createElement('span');
        frame.className = 'cs-fest-imgframe';
        frame.setAttribute('aria-hidden', 'true');
        media.appendChild(frame);
      }
      SPARKS.forEach(function (p) {
        var s = document.createElement('span');
        s.className = 'cs-fest-cspark';
        s.textContent = '✦';
        s.setAttribute('aria-hidden', 'true');
        if (p[0]) s.style.top = p[0];
        if (p[1]) s.style.left = p[1];
        if (p[2]) s.style.right = p[2];
        if (p[3]) s.style.bottom = p[3];
        s.style.animationDelay = (Math.random() * 2.4).toFixed(2) + 's';
        (media || card).appendChild(s);
      });
    }
    function decoAll() { document.querySelectorAll('.cs-pcard').forEach(deco); }

    // Once a second: any [data-cs-fest-cd] countdown, the deal row following
    // the save badge, and at the sale's end every deal row hidden (the price
    // block goes with it and the regular price comes back).
    function p2(n) { return (n < 10 ? '0' : '') + n; }
    function tick() {
      var ms = END - Date.now();
      if (!(ms > 0)) {
        document.querySelectorAll('.cs-fest-deal').forEach(function (d) { d.hidden = true; });
        return;
      }
      var x = Math.floor(ms / 1000);
      var d = Math.floor(x / 86400);
      var txt = (d ? d + 'd ' : '') + p2(Math.floor(x % 86400 / 3600)) + ':' + p2(Math.floor(x % 3600 / 60)) + ':' + p2(x % 60);
      document.querySelectorAll('[data-cs-fest-cd]').forEach(function (b) { b.textContent = txt; });
      document.querySelectorAll('.cs-pcard').forEach(function (c) {
        var save = c.querySelector('[data-cs-save]');
        var pct = c.querySelector('[data-cs-fest-pct]');
        var deal = c.querySelector('.cs-fest-deal');
        if (!save || !pct || !deal) return;
        // \d, not d: the inline copy had lost its backslash and never matched.
        var m = (save.textContent || '').match(/(\d+)%/);
        if (m && !save.hidden) {
          // Only write on a change. Rewriting the same text every second would
          // wake the MutationObserver below for nothing.
          var label = m[1] + '% OFF';
          if (pct.textContent !== label) pct.textContent = label;
          if (deal.hidden) deal.hidden = false;
        } else if (save.hidden && !deal.hidden) {
          deal.hidden = true;
        }
      });
    }
    tick();
    setInterval(tick, 1000);

    decoAll();
    window.addEventListener('load', decoAll);
    // Cards added later (theme editor re-renders) get dressed too.
    if (window.MutationObserver) {
      new MutationObserver(function (records) {
        for (var i = 0; i < records.length; i++) {
          if (records[i].addedNodes.length) { decoAll(); break; }
        }
      }).observe(document.documentElement, { childList: true, subtree: true });
    }
  })();

  /* ---------------------------------------------------------------------
     Festival label roll: one clock for the page (Frido's card ticker).

     Every 2.2s html[data-cs-roll] steps 1 → 2, and cs-pcard.css slides every
     card's label up one line in 450ms. Line 3 copies line 1, so 530ms after
     reaching it the clock sets 0 and the track snaps back with no
     transition: the label only ever rolls upward. Every card reads the same
     attribute, so they move on one beat, and cards added later join in.

     Stopped while the tab is hidden. Never started under reduced motion,
     where the CSS keeps "Festival Price" still.
     --------------------------------------------------------------------- */
  (function labelClock() {
    var root = document.documentElement;
    if (!document.querySelector('.cs-fest-deal__track')) return;
    if (!root.classList.contains('cs-fest-live') || !root.classList.contains('cs-fest-sale') || reduceMotion.matches) return;

    var step = 0;
    var beat = null;
    var snap = null;
    function set(n) {
      step = n;
      root.setAttribute('data-cs-roll', String(n));
    }
    function advance() {
      set(step + 1);
      if (step === 2) snap = setTimeout(function () { set(0); }, 530);
    }
    function start() {
      if (!beat && !document.hidden) beat = setInterval(advance, 2200);
    }
    function stop() {
      clearInterval(beat);
      clearTimeout(snap);
      beat = snap = null;
      if (step === 2) set(0); // never park on the copy
    }
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop(); else start();
    });
    start();
  })();

  /* ---------------------------------------------------------------------
     Combo ADD TO CART shine: only runs where it can be seen.

     One IntersectionObserver for every shine on the page; a shine runs
     while it is on screen and the tab is visible, and is paused otherwise.
     Under reduced motion nothing is observed (the CSS does not draw it).
     --------------------------------------------------------------------- */
  (function ctaShine() {
    if (reduceMotion.matches) return;
    var shines = document.getElementsByClassName('cs-pcard__shine'); // live list
    if (!shines.length) return;

    var onScreen = [];
    function paint() {
      for (var i = 0; i < shines.length; i++) {
        shines[i].classList.toggle('is-live', !document.hidden && onScreen.indexOf(shines[i]) !== -1);
      }
    }

    if (!('IntersectionObserver' in window)) {
      // No observer: let them run, still pausing with the tab.
      onScreen = Array.prototype.slice.call(shines);
      document.addEventListener('visibilitychange', paint);
      paint();
      return;
    }

    // Watch the BUTTON, not the shine: a paused shine is parked outside the
    // button's clip, so it never "intersects" and would never start.
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        var s = e.target.querySelector('.cs-pcard__shine');
        if (!s) return;
        var at = onScreen.indexOf(s);
        if (e.isIntersecting && at === -1) onScreen.push(s);
        if (!e.isIntersecting && at !== -1) onScreen.splice(at, 1);
      });
      paint();
    });
    function watch() {
      for (var i = 0; i < shines.length; i++) io.observe(shines[i].parentNode);
    }
    watch();
    // The theme editor re-renders sections in place; pick up their shines.
    document.addEventListener('shopify:section:load', watch);
    document.addEventListener('visibilitychange', paint);
  })();
})();
